import { useEffect, useRef, useState } from 'react';
import { PageHeader } from '../components/Layout.jsx';
import { Alert, Card, Empty, ErrorState, Input, Loading, Segmented, Select, Stat } from '../components/ui.jsx';
import { Modal } from '../components/Modal.jsx';
import { DocumentActions } from '../components/DocumentActions.jsx';
import { api } from '../lib/api.js';
import { useApi } from '../lib/hooks.js';
import { useAuth } from '../lib/auth.jsx';
import { useToast } from '../lib/toast.jsx';
import { t } from '../lib/i18n.js';
import { daysAgo, formatDate, liters, money, moneyPair, num, today, tons } from '../lib/format.js';

export default function Reports() {
  const { settings } = useAuth();
  const [from, setFrom] = useState(daysAgo(29));
  const [to, setTo] = useState(today());
  const [agingParty, setAgingParty] = useState('customer');
  const docRef = useRef(null);

  const summary = useApi('/api/reports/summary', { from, to });
  const daily = useApi('/api/reports/daily', { from, to });
  const aging = useApi('/api/reports/aging', { party_type: agingParty, as_of: to });

  const s = summary.data;

  return (
    <>
      <PageHeader title={t.report.title} subtitle={t.report.subtitle} />

      <div className="page">
        <Card>
          <div className="filters">
            <Input label={t.common.from} type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
            <Input label={t.common.to} type="date" value={to} onChange={(e) => setTo(e.target.value)} />
            <div className="field">
              <label>{t.report.quickRange}</label>
              <div className="btn-row">
                <button type="button" className="btn btn-sm" onClick={() => { setFrom(daysAgo(6)); setTo(today()); }}>
                  {t.report.days7}
                </button>
                <button type="button" className="btn btn-sm" onClick={() => { setFrom(daysAgo(29)); setTo(today()); }}>
                  {t.report.days30}
                </button>
                <button
                  type="button"
                  className="btn btn-sm"
                  onClick={() => { setFrom(`${today().slice(0, 4)}-01-01`); setTo(today()); }}
                >
                  {t.report.year}
                </button>
              </div>
            </div>
          </div>
        </Card>

        <ErrorState error={summary.error} onRetry={summary.reload} />
        {summary.loading && !s && <Loading />}

        {s && (
          <>
            <DocumentActions
              targetRef={docRef}
              paged
              filename={`report-${from}-to-${to}.pdf`}
              whatsappText={reportText(s, settings)}
            />

            <div className="doc" ref={docRef}>
              <div className="doc-head">
                <img src="/logo.png" alt="" className="doc-logo" />
                <div className="brand">
                  <div className="name">{settings.facility_name || t.app.name}</div>
                  <div className="contact">
                    {formatDate(from)} — {formatDate(to)}
                  </div>
                </div>
                <div className="ref">
                  <div className="kind">{t.report.periodReport}</div>
                </div>
              </div>

              <div className="table-wrap" style={{ marginTop: 12 }}>
                <table>
                  <thead>
                    <tr>
                      <th>{t.report.line}</th>
                      <th className="num">{t.report.iqd}</th>
                      <th className="num">{t.report.usd}</th>
                      <th className="num">{t.report.volumeCol}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>{t.report.dieselSold(num(s.sales.count))}</td>
                      <td className="num">{num(s.sales.iqd)}</td>
                      <td className="num">{num(s.sales.usd)}</td>
                      <td className="num">{liters(s.sales.liters)}</td>
                    </tr>
                    <tr>
                      <td>{t.report.dieselBought(num(s.purchases.count))}</td>
                      <td className="num">({num(s.purchases.iqd)})</td>
                      <td className="num">({num(s.purchases.usd)})</td>
                      <td className="num">
                        {tons(s.purchases.tons)} / {liters(s.purchases.liters)}
                      </td>
                    </tr>
                    <tr>
                      <td>{t.report.expenses}</td>
                      <td className="num">({num(s.expenses.iqd)})</td>
                      <td className="num">({num(s.expenses.usd)})</td>
                      <td className="num">-</td>
                    </tr>
                    <tr>
                      <td>{t.report.salariesPaid}</td>
                      <td className="num">({num(s.salaries.iqd)})</td>
                      <td className="num">({num(s.salaries.usd)})</td>
                      <td className="num">-</td>
                    </tr>
                  </tbody>
                  <tfoot>
                    <tr>
                      <td>{t.report.netForPeriod}</td>
                      <td className="num">{num(s.net.iqd)}</td>
                      <td className="num">{num(s.net.usd)}</td>
                      <td className="num">-</td>
                    </tr>
                  </tfoot>
                </table>
              </div>

              <p className="small" style={{ marginTop: 10, color: '#64748b' }}>
                {t.report.netNote}
              </p>

              <div className="table-wrap" style={{ marginTop: 12 }}>
                <table>
                  <thead>
                    <tr>
                      <th>{t.report.settlements}</th>
                      <th className="num">{t.report.iqd}</th>
                      <th className="num">{t.report.usd}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>{t.report.receivedFromCustomers}</td>
                      <td className="num">{num(s.payments_in.iqd)}</td>
                      <td className="num">{num(s.payments_in.usd)}</td>
                    </tr>
                    <tr>
                      <td>{t.report.paidToSuppliers}</td>
                      <td className="num">{num(s.payments_out.iqd)}</td>
                      <td className="num">{num(s.payments_out.usd)}</td>
                    </tr>
                    <tr>
                      <td>{t.report.soldOnCredit}</td>
                      <td className="num">{num(s.sales.debt_iqd)}</td>
                      <td className="num">{num(s.sales.debt_usd)}</td>
                    </tr>
                    <tr>
                      <td>{t.report.boughtOnCredit}</td>
                      <td className="num">{num(s.purchases.debt_iqd)}</td>
                      <td className="num">{num(s.purchases.debt_usd)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div className="stat-grid">
              <Stat
                label={t.report.sold}
                value={liters(s.sales.liters)}
                hint={moneyPair(s.sales.iqd, s.sales.usd, { zero: money(0, 'IQD') })}
              />
              <Stat
                label={t.report.bought}
                value={liters(s.purchases.liters)}
                hint={moneyPair(s.purchases.iqd, s.purchases.usd, { zero: money(0, 'IQD') })}
              />
              <Stat
                label={t.report.netIqd}
                value={money(s.net.iqd, 'IQD')}
                tone={Number(s.net.iqd) >= 0 ? 'success' : 'danger'}
              />
              <Stat
                label={t.report.netUsd}
                value={money(s.net.usd, 'USD')}
                tone={Number(s.net.usd) >= 0 ? 'success' : 'danger'}
              />
            </div>

            {daily.data && <VolumeChart rows={daily.data.rows} />}

            <div className="stat-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))' }}>
              <Card title={t.report.topCustomers}>
                <TopTable rows={s.top_customers} countKey="sales_count" />
              </Card>
              <Card title={t.report.topSuppliers}>
                <TopTable rows={s.top_suppliers} countKey="purchase_count" />
              </Card>
            </div>
          </>
        )}

        <Card
          title={t.report.aging}
          actions={
            <Segmented
              value={agingParty}
              onChange={setAgingParty}
              options={[
                { value: 'customer', label: t.nav.customers },
                { value: 'supplier', label: t.nav.suppliers },
              ]}
            />
          }
        >
          <p className="small muted mt-0">{t.report.agingNote(formatDate(to))}</p>
          <ErrorState error={aging.error} onRetry={aging.reload} />
          {aging.loading && !aging.data && <Loading />}
          {aging.data && aging.data.parties.length === 0 && <Empty title={t.report.nothingOutstanding} />}
          {aging.data && aging.data.parties.length > 0 && (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>{t.report.account}</th>
                    <th>{t.common.currency}</th>
                    {aging.data.buckets.map((b) => (
                      <th key={b} className="num">
                        {b} {t.report.days}
                      </th>
                    ))}
                    <th className="num">{t.common.total}</th>
                    <th>{t.report.oldest}</th>
                  </tr>
                </thead>
                <tbody>
                  {aging.data.parties.flatMap((party) =>
                    ['IQD', 'USD']
                      .filter((cur) => Number(party[cur.toLowerCase()].outstanding) !== 0)
                      .map((cur) => {
                        const detail = party[cur.toLowerCase()];
                        return (
                          <tr key={`${party.party_id}-${cur}`}>
                            <td className="wrap">{party.name}</td>
                            <td>{cur === 'IQD' ? t.report.iqd : t.report.usd}</td>
                            {aging.data.buckets.map((b) => (
                              <td key={b} className="num">
                                {Number(detail.buckets[b]) === 0 ? '-' : num(detail.buckets[b])}
                              </td>
                            ))}
                            <td className="num strong">{num(detail.outstanding)}</td>
                            <td>{detail.oldest_unpaid ? formatDate(detail.oldest_unpaid) : '-'}</td>
                          </tr>
                        );
                      }),
                  )}
                </tbody>
                <tfoot>
                  {['IQD', 'USD'].map((cur) => (
                    <tr key={cur}>
                      <td>{t.common.total}</td>
                      <td>{cur === 'IQD' ? t.report.iqd : t.report.usd}</td>
                      {aging.data.buckets.map((b) => (
                        <td key={b} className="num">
                          {num(aging.data.totals[cur][b])}
                        </td>
                      ))}
                      <td className="num">{num(aging.data.totals[cur].total)}</td>
                      <td />
                    </tr>
                  ))}
                </tfoot>
              </table>
            </div>
          )}
        </Card>

        <BatchProfitabilityCard from={from} to={to} />
      </div>
    </>
  );
}

/** What each batch (a delivery) cost against what has actually been sold
 * against it so far. Coverage is often partial - sales opt into a batch by
 * hand, since tanks pool fuel from every purchase that ever fed them. */
function BatchProfitabilityCard({ from, to }) {
  const { isAdmin } = useAuth();
  const [supplierId, setSupplierId] = useState('');
  const [search, setSearch] = useState('');
  const [distributing, setDistributing] = useState(null);
  const suppliers = useApi('/api/suppliers', { include_inactive: true });
  const batches = useApi('/api/reports/batch-profitability', { from, to, supplier_id: supplierId, q: search });

  return (
    <Card title={t.batchProfit.title}>
      <p className="small muted mt-0">{t.batchProfit.note}</p>
      <div className="filters">
        <Select
          label={t.purchase.supplier}
          placeholder={t.batchProfit.allSuppliers}
          value={supplierId}
          onChange={(e) => setSupplierId(e.target.value)}
          options={(suppliers.data || []).map((s) => ({ value: s.id, label: s.name }))}
        />
        <Input
          className="grow"
          label={t.common.search}
          placeholder={t.batchProfit.searchHint}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      <ErrorState error={batches.error} onRetry={batches.reload} />
      {batches.loading && !batches.data && <Loading />}
      {batches.data && batches.data.rows.length === 0 && <Empty title={t.batchProfit.none} />}
      {batches.data && batches.data.rows.length > 0 && (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>{t.purchase.batchNo}</th>
                <th>{t.purchase.supplier}</th>
                <th>{t.common.date}</th>
                <th className="num">{t.batchProfit.coverage}</th>
                <th className="num">{t.batchProfit.cost}</th>
                <th className="num">{t.batchProfit.revenue}</th>
                <th className="num">{t.batchProfit.profit}</th>
                <th className="num">{t.batchProfit.undistributed}</th>
                <th>{t.batchProfit.flags}</th>
                {isAdmin && <th />}
              </tr>
            </thead>
            <tbody>
              {batches.data.rows.map((row) => {
                const currency = row.cost_currency;
                const cost = currency === 'IQD' ? row.cost_iqd : row.cost_usd;
                const revenue = currency === 'IQD' ? row.revenue_iqd : row.revenue_usd;
                const profit = currency === 'IQD' ? row.profit_iqd : row.profit_usd;
                const undistributed = currency === 'IQD' ? row.undistributed_iqd : row.undistributed_usd;
                const canDistribute = !row.mixed_currency && Number(undistributed) !== 0;
                return (
                  <tr key={row.purchase_id}>
                    <td className="wrap">{row.batch_no}</td>
                    <td className="wrap">{row.supplier_name}</td>
                    <td>{formatDate(row.purchased_on)}</td>
                    <td className="num">{row.coverage_pct}٪</td>
                    <td className="num">{money(cost, currency)}</td>
                    <td className="num">{moneyPair(row.revenue_iqd, row.revenue_usd, { zero: '-' })}</td>
                    <td className={`num strong ${Number(profit) < 0 ? 'neg' : 'pos'}`}>{money(profit, currency)}</td>
                    <td className="num">
                      {Number(undistributed) === 0 ? '-' : money(undistributed, currency)}
                      {row.stale && <span className="badge muted" style={{ marginInlineStart: 4 }}>{t.batchProfit.stale}</span>}
                    </td>
                    <td>
                      {row.mixed_currency && <span className="badge debt">{t.batchProfit.mixedCurrency}</span>}
                      {row.over_allocated && <span className="badge debt">{t.batchProfit.overAllocated}</span>}
                      {row.tank_mismatch && <span className="badge muted">{t.batchProfit.tankMismatch}</span>}
                    </td>
                    {isAdmin && (
                      <td>
                        {canDistribute && (
                          <button type="button" className="btn btn-sm" onClick={() => setDistributing(row)}>
                            {t.batchProfit.distribute}
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {distributing && (
        <DistributeModal
          batch={distributing}
          onClose={() => setDistributing(null)}
          onDistributed={() => {
            setDistributing(null);
            batches.reload();
          }}
        />
      )}
    </Card>
  );
}

function DistributeModal({ batch, onClose, onDistributed }) {
  const toast = useToast();
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    api
      .post(`/api/reports/batch-profitability/${batch.purchase_id}/distribute`, { dry_run: true })
      .then((res) => setPreview(res))
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const confirm = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/reports/batch-profitability/${batch.purchase_id}/distribute`, {});
      toast.success(t.batchProfit.distributed(batch.batch_no));
      onDistributed();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      title={t.batchProfit.distributeTitle(batch.batch_no)}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose} disabled={busy}>
            {t.common.cancel}
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={confirm}
            disabled={busy || loading || !preview || preview.lines.length === 0}
          >
            {busy ? <span className="spinner" /> : t.batchProfit.confirmDistribute}
          </button>
        </>
      }
    >
      {error && <Alert tone="error">{error}</Alert>}
      {loading && <Loading />}
      {preview && (
        <>
          <p className="small muted mt-0">{t.batchProfit.distributePreviewNote(money(preview.undistributed, preview.currency))}</p>
          {preview.lines.length === 0 ? (
            <Empty title={t.batchProfit.nothingToDistribute} />
          ) : (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>{t.shareholder.title}</th>
                    <th className="num">{t.common.amount}</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.lines.map((line) => (
                    <tr key={line.shareholder_id}>
                      <td>{line.shareholder_name}</td>
                      <td className={`num ${Number(line.amount) < 0 ? 'neg' : ''}`}>{money(line.amount, preview.currency)}</td>
                    </tr>
                  ))}
                </tbody>
                {Number(preview.unallocated) !== 0 && (
                  <tfoot>
                    <tr>
                      <td>{t.batchProfit.unallocated}</td>
                      <td className="num muted">{money(preview.unallocated, preview.currency)}</td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          )}
        </>
      )}
    </Modal>
  );
}

function TopTable({ rows, countKey }) {
  if (!rows?.length) return <Empty title={t.report.nothingInPeriod} />;
  return (
    <div className="table-wrap">
      <table>
        <thead>
          <tr>
            <th>{t.common.name}</th>
            <th className="num">{t.report.count}</th>
            <th className="num">{t.report.litersCol}</th>
            <th className="num">{t.report.valueCol}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.id}>
              <td className="wrap">{row.name}</td>
              <td className="num">{num(row[countKey])}</td>
              <td className="num">{liters(row.liters)}</td>
              <td className="num">{moneyPair(row.iqd, row.usd, { zero: '-' })}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Litres in and out per day. Plain SVG - no chart library to load offline. */
function VolumeChart({ rows }) {
  if (!rows?.length) return null;
  const max = Math.max(1, ...rows.map((r) => Math.max(Number(r.bought_liters), Number(r.sold_liters))));
  const width = Math.max(rows.length * 14, 280);
  const height = 140;
  const barWidth = width / rows.length / 2.6;

  return (
    <Card title={t.report.dailyVolume}>
      <div className="table-wrap">
        {/* The chart itself stays left-to-right: time runs forward along the
            x-axis the same way it does on every other chart the operator sees. */}
        <svg
          viewBox={`0 0 ${width} ${height + 22}`}
          width="100%"
          height={height + 22}
          role="img"
          aria-label={t.report.dailyAria}
          style={{ direction: 'ltr' }}
        >
          {[0.25, 0.5, 0.75, 1].map((f) => (
            <line
              key={f}
              x1="0"
              x2={width}
              y1={height - f * height}
              y2={height - f * height}
              stroke="currentColor"
              strokeOpacity="0.12"
            />
          ))}
          {rows.map((row, index) => {
            const x = (index / rows.length) * width;
            const boughtHeight = (Number(row.bought_liters) / max) * height;
            const soldHeight = (Number(row.sold_liters) / max) * height;
            return (
              <g key={row.day}>
                <rect x={x} y={height - boughtHeight} width={barWidth} height={boughtHeight} fill="#3b82f6" rx="1">
                  <title>{`${row.day}: ${t.report.boughtLegend} ${liters(row.bought_liters)}`}</title>
                </rect>
                <rect
                  x={x + barWidth + 1}
                  y={height - soldHeight}
                  width={barWidth}
                  height={soldHeight}
                  fill="#0d9488"
                  rx="1"
                >
                  <title>{`${row.day}: ${t.report.soldLegend} ${liters(row.sold_liters)}`}</title>
                </rect>
              </g>
            );
          })}
          <line x1="0" x2={width} y1={height} y2={height} stroke="currentColor" strokeOpacity="0.3" />
        </svg>
      </div>
      <div className="row wrap small muted">
        <span className="row">
          <span style={{ width: 10, height: 10, background: '#3b82f6', borderRadius: 2 }} /> {t.report.boughtLegend}
        </span>
        <span className="row">
          <span style={{ width: 10, height: 10, background: '#0d9488', borderRadius: 2 }} /> {t.report.soldLegend}
        </span>
        <span className="spacer" />
        <span>{t.report.peakDay(liters(max))}</span>
      </div>
    </Card>
  );
}

function reportText(s, settings) {
  return [
    `*${settings.facility_name || t.app.name}*`,
    `${t.report.periodReport} ${formatDate(s.from)} - ${formatDate(s.to)}`,
    '',
    `${t.report.sold}: ${liters(s.sales.liters)} — ${moneyPair(s.sales.iqd, s.sales.usd, { zero: '0' })}`,
    `${t.report.bought}: ${liters(s.purchases.liters)} — ${moneyPair(s.purchases.iqd, s.purchases.usd, { zero: '0' })}`,
    `${t.report.expenses}: ${moneyPair(s.expenses.iqd, s.expenses.usd, { zero: '0' })}`,
    `${t.report.salariesPaid}: ${moneyPair(s.salaries.iqd, s.salaries.usd, { zero: '0' })}`,
    '',
    `${t.report.netForPeriod}: ${money(s.net.iqd, 'IQD')} / ${money(s.net.usd, 'USD')}`,
  ].join('\n');
}
